import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import { HandleAvatar } from "@/components/HandleAvatar";
import { PostComposer, WallPost, WallHabits } from "@/components/community/WallParts";
import { SampleTag, SAMPLE_GARDEN_LINE } from "@/components/community/SampleTag";

export function MemberWall({ category, member, onClose }) {
  const [wall, setWall] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/community/${category}/posts/${member.id}`);
    setWall(r.data);
  }, [category, member.id]);
  useEffect(() => { load(); }, [load]);

  const send = async (kind, text) => {
    try {
      const r = await api.post(`/community/${category}/posts`, { to_user_id: member.id, kind, text });
      toast.success(kind === "support" ? "Support sent. That matters." : "Suggestion shared.");
      (r.data.achievements || []).forEach((a) => toast.success(`Achievement unlocked: ${a.title} (+${a.points})`));
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return false;
    }
  };

  const flag = async (postId, reason) => {
    const r = await api.post(`/community/posts/${postId}/flag`, { reason });
    toast(r.data.removed ? "Note removed. Thank you for keeping this space kind." : "Flagged for review.");
    load();
  };

  const remove = async (postId) => {
    await api.delete(`/community/posts/${postId}`);
    load();
  };

  if (!wall) return null;
  const accepts = wall.member.accept_suggestions;

  return (
    <div className="warm-card p-5 space-y-4" data-testid={`member-wall-${member.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HandleAvatar handle={wall.member.handle} />
          <div>
            <div className="font-display font-bold text-stone-900 flex items-center gap-2" data-testid="wall-handle">{wall.member.handle}'s wall{member.is_sample && <SampleTag id={`wall-${member.id}`} />}</div>
            {member.is_sample && <div className="text-xs text-stone-500 italic" data-testid="wall-sample-line">{SAMPLE_GARDEN_LINE}</div>}
            <div className="text-xs text-stone-500" data-testid="wall-suggestion-status">
              {accepts ? "Open to suggestions and support" : "Support only — hasn't opted in to suggestions"}
            </div>
          </div>
        </div>
        <button onClick={onClose} data-testid="wall-close" className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
      </div>

      <WallHabits habits={wall.member.habits} accepts={accepts} isMe={member.is_me} />
      {!member.is_me && <PostComposer accepts={accepts} memberHandle={wall.member.handle} onSend={send} />}

      <ul className="space-y-2">
        {wall.posts.length === 0 && <li className="text-sm text-stone-500">No notes yet. Be the first to cheer.</li>}
        {wall.posts.map((p) => <WallPost key={p.id} post={p} onFlag={flag} onDelete={remove} />)}
      </ul>
    </div>
  );
}
